import React, { useEffect, useState, Children, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import {
  X,
  Heart,
  Globe,
  User,
  Settings,
  LogOut,
  LogIn,
  UserPlus,
  ChevronDown,
  Calendar,
  ChevronRight,
  Link as LinkIcon } from
'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useMenuConfig } from '../contexts/MenuConfigContext';
interface MobileNavDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  favoriteCount: number;
}
export function MobileNavDrawer({
  isOpen,
  onClose,
  favoriteCount
}: MobileNavDrawerProps) {
  const location = useLocation();
  const { lang, toggleLang, ui, isRTL } = useLanguage();
  const { user, signOut } = useAuth();
  const menus = useMenuConfig().config;
  const isAr = lang === 'ar';
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const headerSettings = menus.headerSettings || {
    logoUrlEn: "/3.svg",

    logoUrlAr: "/3.svg",

    logoHeight: 80,
    logoAltEn: 'Alex Hidden Gems',
    logoAltAr: 'أليكس هيدن جيمز'
  };
  const currentLogoUrl = isAr ?
  headerSettings.logoUrlAr || headerSettings.logoUrlEn :
  headerSettings.logoUrlEn;
  const currentLogoAlt = isAr ?
  headerSettings.logoAltAr :
  headerSettings.logoAltEn;
  // Close drawer on route change.
  // IMPORTANT: depend only on `location.pathname`. If `onClose` is included in
  // the deps array and the parent passes a fresh inline arrow on every render,
  // the effect re-fires on every render and slams the drawer shut the instant
  // the user opens it. The eslint-disable comment is intentional and required.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    onClose();
  }, [location.pathname]);
  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  return (
    <AnimatePresence>
      {isOpen &&
      <>
          <motion.div
          initial={{
            opacity: 0
          }}
          animate={{
            opacity: 1
          }}
          exit={{
            opacity: 0
          }}
          onClick={onClose}
          className="fixed inset-0 bg-sea-blue/40 backdrop-blur-sm z-[100] md:hidden" />
        
          <motion.div
          initial={{
            x: isRTL ? '100%' : '-100%'
          }}
          animate={{
            x: 0
          }}
          exit={{
            x: isRTL ? '100%' : '-100%'
          }}
          transition={{
            type: 'spring',
            damping: 25,
            stiffness: 200
          }}
          className={`fixed top-0 bottom-0 ${isRTL ? 'right-0' : 'left-0'} w-[85vw] max-w-[360px] bg-white z-[101] md:hidden flex flex-col shadow-2xl`}>
          
            <div className="flex items-center justify-between p-4 border-b border-sea-blue/10">
              <img
              src={currentLogoUrl}
              alt={currentLogoAlt}
              className="h-10 w-auto" />
            
              <button
              onClick={onClose}
              className="p-2 text-sea-blue/80 hover:text-terracotta hover:bg-sand rounded-full transition-colors">
              
                <X size={24} />
              </button>
            </div>

            <div className="flex-grow overflow-y-auto py-4 hide-scrollbar">
              {/* Dynamic Navigation */}
              <nav className="px-4 space-y-1">
                {menus.header.
              filter((item) => item.visible !== false).
              map((item) => {
                const hasChildren =
                item.children && item.children.length > 0;
                const isActive = location.pathname === item.url;
                const isExpanded = expandedId === item.id;
                if (hasChildren) {
                  return (
                    <div key={item.id} className="mb-2">
                          <button
                        onClick={() =>
                        setExpandedId(isExpanded ? null : item.id)
                        }
                        className="w-full flex items-center justify-between px-4 py-3.5 rounded-xl transition-colors text-sea-blue hover:bg-sand font-medium">
                        
                            <span
                          className={`text-lg font-bold ${isAr ? 'font-arabic' : ''}`}>
                          
                              {isAr ? item.label.ar : item.label.en}
                            </span>
                            <ChevronDown
                          size={20}
                          className={`text-sea-blue/50 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                        
                          </button>

                          <AnimatePresence>
                            {isExpanded &&
                        <motion.div
                          initial={{
                            height: 0,
                            opacity: 0
                          }}
                          animate={{
                            height: 'auto',
                            opacity: 1
                          }}
                          exit={{
                            height: 0,
                            opacity: 0
                          }}
                          className="overflow-hidden">
                          
                                <div className="pl-4 pr-2 py-2 space-y-1 border-l-2 border-sea-blue/10 ml-6 mt-1">
                                  {item.
                            children!.filter(
                              (c) => c.visible !== false
                            ).
                            map((child) => {
                              const linkProps =
                              child.type === 'external' ?
                              {
                                href: child.url,
                                target: child.openInNewTab ?
                                '_blank' :
                                undefined,
                                rel: child.openInNewTab ?
                                'noopener noreferrer' :
                                undefined
                              } :
                              {
                                to: child.url
                              };
                              const LinkComponent =
                              child.type === 'external' ? 'a' : Link;
                              return (
                                <LinkComponent
                                  key={child.id}
                                  {...linkProps as any}
                                  className="flex items-center justify-between px-4 py-3 rounded-xl text-sea-blue hover:bg-sand transition-colors">
                                  
                                          <span
                                    className={`text-base font-medium ${isAr ? 'font-arabic' : ''}`}>
                                    
                                            {isAr ?
                                    child.label.ar :
                                    child.label.en}
                                          </span>
                                          <ChevronRight
                                    size={16}
                                    className={`text-sea-blue/30 ${isAr ? 'rotate-180' : ''}`} />
                                  
                                        </LinkComponent>);

                            })}
                                </div>
                              </motion.div>
                        }
                          </AnimatePresence>
                        </div>);

                }
                const linkProps =
                item.type === 'external' ?
                {
                  href: item.url,
                  target: item.openInNewTab ? '_blank' : undefined,
                  rel: item.openInNewTab ?
                  'noopener noreferrer' :
                  undefined
                } :
                {
                  to: item.url
                };
                const LinkComponent = item.type === 'external' ? 'a' : Link;
                return (
                  <LinkComponent
                    key={item.id}
                    {...linkProps as any}
                    className={`flex items-center gap-4 px-4 py-3.5 rounded-xl transition-colors ${isActive ? 'bg-sea-blue text-white font-bold' : 'text-sea-blue hover:bg-sand font-medium'}`}>
                    
                        <span
                      className={`text-lg ${isAr ? 'font-arabic' : ''}`}>
                      
                          {isAr ? item.label.ar : item.label.en}
                        </span>
                      </LinkComponent>);

              })}
              </nav>

              <div className="mx-8 h-px bg-sea-blue/10 my-4" />

              {/* Utility Links */}
              <div className="px-4 space-y-1">
                <Link
                to="/favorites"
                className="flex items-center justify-between px-4 py-3.5 rounded-xl text-sea-blue hover:bg-sand font-medium transition-colors">
                
                  <div className="flex items-center gap-4">
                    <Heart size={20} className="text-terracotta" />
                    <span className={`text-lg ${isAr ? 'font-arabic' : ''}`}>
                      {ui.favorites}
                    </span>
                  </div>
                  {favoriteCount > 0 &&
                <span className="bg-terracotta text-white text-xs font-bold px-2 py-0.5 rounded-full">
                      {favoriteCount}
                    </span>
                }
                </Link>

                <button
                onClick={toggleLang}
                className="w-full flex items-center gap-4 px-4 py-3.5 rounded-xl text-sea-blue hover:bg-sand font-medium transition-colors">
                
                  <Globe size={20} className="text-sea-blue/50" />
                  <span className={`text-lg ${isAr ? 'font-arabic' : ''}`}>
                    {isAr ? 'English' : 'العربية'}
                  </span>
                </button>
              </div>

              <div className="mx-8 h-px bg-sea-blue/10 my-4" />

              {/* Auth Section */}
              <div className="px-4 pb-8">
                {user ?
              <div className="bg-sand/30 rounded-2xl p-4 border border-sea-blue/5">
                    <div className="flex items-center gap-3 mb-4">
                      {user.avatar ?
                  <img
                    src={user.avatar}
                    alt={user.displayName}
                    className="w-10 h-10 rounded-full object-cover" /> :


                  <div className="w-10 h-10 rounded-full bg-terracotta text-white flex items-center justify-center font-bold text-sm">
                          {(user.displayName || user.email).
                    charAt(0).
                    toUpperCase()}
                        </div>
                  }
                      <div className="min-w-0">
                        <div
                      className={`font-bold text-sea-blue truncate ${isAr ? 'font-arabic' : ''}`}>
                      
                          {user.displayName}
                        </div>
                        <div className="text-xs text-sea-blue/80 truncate ltr-numerals">
                          {user.email}
                        </div>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Link
                    to={`/u/${user.username}`}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-sea-blue hover:bg-white transition-colors">
                    
                        <User size={16} className="text-sea-blue/50" />
                        <span className={isAr ? 'font-arabic' : ''}>
                          {isAr ? 'صفحتي' : 'Profile'}
                        </span>
                      </Link>
                      <Link
                    to="/account/plans"
                    className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-sea-blue hover:bg-white transition-colors">
                    
                        <Calendar size={16} className="text-sea-blue/50" />
                        <span className={isAr ? 'font-arabic' : ''}>
                          {isAr ? 'خططك' : 'Your Plans'}
                        </span>
                      </Link>
                      <Link
                    to="/account/settings"
                    className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-sea-blue hover:bg-white transition-colors">
                    
                        <Settings size={16} className="text-sea-blue/50" />
                        <span className={isAr ? 'font-arabic' : ''}>
                          {isAr ? 'الإعدادات' : 'Settings'}
                        </span>
                      </Link>
                      <button
                    onClick={() => {
                      signOut();
                      onClose();
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-terracotta hover:bg-terracotta/10 transition-colors">
                    
                        <LogOut size={16} />
                        <span className={isAr ? 'font-arabic' : ''}>
                          {isAr ? 'تسجيل الخروج' : 'Sign out'}
                        </span>
                      </button>
                    </div>
                  </div> :

              <div className="grid grid-cols-2 gap-3">
                    <Link
                  to="/auth/signin"
                  className="flex items-center justify-center gap-2 bg-sand hover:bg-sand-dark text-sea-blue px-4 py-3 rounded-xl font-bold transition-colors">
                  
                      <LogIn size={18} />
                      <span className={isAr ? 'font-arabic' : ''}>
                        {isAr ? 'دخول' : 'Sign in'}
                      </span>
                    </Link>
                    <Link
                  to="/auth/signup"
                  className="flex items-center justify-center gap-2 bg-sea-blue hover:bg-sea-blue-light text-white px-4 py-3 rounded-xl font-bold transition-colors">
                  
                      <UserPlus size={18} />
                      <span className={isAr ? 'font-arabic' : ''}>
                        {isAr ? 'حساب جديد' : 'Sign up'}
                      </span>
                    </Link>
                  </div>
              }
              </div>
            </div>
          </motion.div>
        </>
      }
    </AnimatePresence>);

}